import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import PeerConnectionManager from "./PeerConnectionManager";
import { USER_ROLE, EVENTS } from "./enums/LiveStreamingEnums";
import Logger from "./Logger";
import LoggerService from "./LoggerService";
import CustomErrorManager from "common/services/CustomErrorManager.js";
import CustomErrorCodeMessages from "common/enums/CustomErrorMessages.js";
import CustomErrorCodes from "common/enums/CustomErrorCodes.js";

import LiveStreaming from "./LiveStreaming";
import MediaTrackService from "./MediaTrackService";

export default class SignalingService {
  static connectionURL = "";
  static hubName = "";

  connection = null;
  instance = null;
  static connectionRetryTimeout = 600;
  static connectionRetryIntervalDurationInSeconds = 10;
  static connectionRetryTimerList = [];

  static HTTP_TRANSPORT_TYPE = {
    NONE: 0,
    WEBSOCKETS: 1,
    SERVER_SENT_EVENTS: 2,
    LONG_POLING: 3
  };

  static init(websocketConnectionURL) {
    SignalingService.connectionURL = websocketConnectionURL;
    SignalingService.updateRetryTimers();
  }

  static getInstance() {
    if (this.instance == null) {
      this.instance = new SignalingService();
    }

    return this.instance;
  }

  static updateRetryTimers() {
    const iterations = Math.floor(
      SignalingService.connectionRetryTimeout /
        SignalingService.connectionRetryIntervalDurationInSeconds
    );
    const list = [0, 2000, 3000, 5000];

    for (let i = 0; i < iterations - 1; i++) {
      list.push(SignalingService.connectionRetryIntervalDurationInSeconds * 1000);
    }

    SignalingService.connectionRetryTimerList = list;
  }

  // creates signalr hub connection
  build() {
    try {
      this.connection = new HubConnectionBuilder()
        .configureLogging(LogLevel.None)
        .withUrl(SignalingService.connectionURL, {
          skipNegotiation: true,
          transport: SignalingService.HTTP_TRANSPORT_TYPE.WEBSOCKETS,
          accessTokenFactory: async () => {
            let liveStreamingWebSocketToken = await LiveStreaming.getLiveStreamingWebSocketToken();
            return liveStreamingWebSocketToken;
          }
        })
        .withAutomaticReconnect(SignalingService.connectionRetryTimerList)
        .build();

      return this.connection;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  // start socket connection
  start = async () => {
    try {
      this.build();
      if (this.connection) {
        await this.connection.start();
      } else {
        throw CustomErrorManager.ServiceError(
          CustomErrorCodes.LIVE_STREAMING_FAILED_TO_START,
          CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_START
        );
      }
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_START,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_START
      );
    }
  };

  destroy = async () => {
    this.stop();
    this.connection = null;
    this.instance = null;
  };

  // socket reconnecting event listener
  onReconnecting = async (listener) => {
    try {
      if (this.connection) {
        await this.connection.onreconnecting(listener);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // socket reconnection event listener
  onReconnected = async (listener) => {
    try {
      if (this.connection) {
        await this.connection.onreconnected(listener);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // socket disconnection event listener
  onDisconnected = async (listener) => {
    try {
      if (this.connection) {
        await this.connection.onclose(listener);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // stop socket connection
  stop = async () => {
    try {
      if (this.connection) {
        await this.connection.stop();
      }
    } catch (error) {
      console.log(error);
    }
  };

  // add event listener for an event on socket
  addListener = async (eventName, listener) => {
    try {
      if (this.connection) {
        await this.connection.on(eventName, listener);
      }
    } catch (error) {
      throw new Error(`Failed to add ${eventName} listener`);
    }
  };

  // remove a specific listener for an event on socket
  removeListener = async (eventName, listener) => {
    try {
      if (this.connection?._connectionState == HubConnectionState.Connected) {
        await this.connection.off(eventName, listener);
      }
    } catch (error) {
      throw new Error(`Failed to remove ${eventName} listener`);
    }
  };

  // remove all listeners for an event on socket
  removeAllListeners = async (eventName) => {
    try {
      if (this.connection?._connectionState == HubConnectionState.Connected) {
        await this.connection.off(eventName);
      }
    } catch (error) {
      throw new Error(`Failed to remove ${eventName} listener`);
    }
  };

  async invoke(eventName, params) {
    const MAX_RETRIES = 3;
    const RETRY_DELAY = 1000; // ms

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (
          !this.connection ||
          this.connection._connectionState != HubConnectionState.Connected
        ) {
          throw new Error("Connection is not active");
        }

        const ack = await this.connection.invoke(eventName, params);

        // If server returns acknowledgment object
        if (ack.success === true) {
          return true; // Delivered and acknowledged
        }
        // else {
        //   console.warn(`Attempt ${attempt}: Server NACK`, ack?.message || ack);
        // }
      } catch (err) {
        // console.error(`Attempt ${attempt}: Transport error while sending message:`, err);
      }

      // Delay before retry if more attempts remain
      if (attempt < MAX_RETRIES) {
        // await delay(RETRY_DELAY);
        new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      }
    }

    return false; // All attempts failed
  }

  joinRoom = async (roomId) => {
    if (this.connection?._connectionState == HubConnectionState.Connected) {
      const status = await this.connection.invoke(EVENTS.JoinRoom, roomId);
      return status;
    }
    return false;
  };

  leaveRoom = async (roomId) => {
    if (this.connection?._connectionState == HubConnectionState.Connected) {
      const status = await this.connection.invoke(EVENTS.LeaveRoom, roomId);
      return status;
    }
    return false;
  };

  // listen to media streaming start request
  listenToMediaStreamingStartRequest = async () => {
    try {
      await this.addListener(
        EVENTS.MediaStreamingStartRequest,
        this.mediaStreamingStartRequestListener
      );
    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[SIGNALING] [Failed to send MediaStartRequest]`,
        error
      );
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_LISTEN_TO_MEDIA_START_REQUEST,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_LISTEN_TO_MEDIA_START_REQUEST
      );
    }
  };

  // listen to media streaming stop request
  listenToMediaStreamingStopRequest = async () => {
    try {
      await this.addListener(
        EVENTS.MediaStreamingStopRequest,
        this.mediaStreamingStopRequestListener
      );
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_LISTEN_TO_MEDIA_STOP_REQUEST,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_LISTEN_TO_MEDIA_STOP_REQUEST
      );
    }
  };

  // send media streaming start request
  sendMediaStreamingStartRequest = async (config, requestedStreamsObj) => {
    let launchId = config.launchId;
    let roomId = config.roomId;
    let peerId = config.peerId;

    let requestedStreams = JSON.stringify(requestedStreamsObj);
    let dataObj = {
      launchId,
      peerId,
      requestedStreams
    };

    try {
      const message = this.formatDataToMessage(roomId, dataObj);
      const status = await this.invoke(EVENTS.MediaStreamingStartRequest, message);

      if (status) {
        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [peerId: ${config.peerId}] [${requestedStreamsObj.webcam ? "WEBCAM" : "SCREEN"}] [OUT] [START REQUEST SENT]`
        );
      } else {
        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [peerId: ${config.peerId}] [${requestedStreamsObj.webcam ? "WEBCAM" : "SCREEN"}] [OUT] [Failed to send START REQUEST]`
        );
      }

      return status;
    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${peerId}] [SIGNALING] [Failed to send media start request for: roomId="${roomId}" peerId="${peerId}" streams="${JSON.stringify(requestedStreamsObj)}]`,
        error
      );
      
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_SEND_MEDIA_START_REQUEST,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_SEND_MEDIA_START_REQUEST,
        error
      );
    }
  };

  // send media streaming stop request
  sendMediaStreamingStopRequest = async (config, requestedStreamsObj) => {
    let launchId = config.launchId;
    let roomId = config.roomId;
    let peerId = config.peerId;
    let requestedStreams = JSON.stringify(requestedStreamsObj);
    let dataObj = {
      launchId,
      peerId,
      requestedStreams
    };
    
    try {
      const message = this.formatDataToMessage(roomId, dataObj);
      const status = await this.invoke(EVENTS.MediaStreamingStopRequest, message);

      if (status) {

        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [peerId: ${config.peerId}] [${requestedStreamsObj.webcam ? "WEBCAM" : "SCREEN"}] [OUT] [STOP REQUEST SENT]`
        );
      } else {
        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [peerId: ${config.peerId}] [${requestedStreamsObj.webcam ? "WEBCAM" : "SCREEN"}] [OUT] [STOP REQUEST SENT]`
        );
      }

      return status;
    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${peerId}] [SIGNALING] [Failed to send media start request for: roomId="${roomId}" peerId="${peerId}" streams="${JSON.stringify(requestedStreamsObj)}]`,
        error
      );

      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_SEND_MEDIA_STOP_REQUEST,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_SEND_MEDIA_STOP_REQUEST
      );
    }
  };

  // remove media streaming start listener
  removeMediaStreamingStartRequestListener = async () => {
    try {
      await this.removeListener(
        EVENTS.MediaStreamingStartRequest,
        this.mediaStreamingStartRequestListener
      );
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_REMOVE_MEDIA_START_REQUEST_LISTENER,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_REMOVE_MEDIA_START_REQUEST_LISTENER
      );
    }
  };

  // remove media streaming start listener
  removeMediaStreamingStopRequestListener = async () => {
    try {
      await this.removeListener(
        EVENTS.MediaStreamingStopRequest,
        this.mediaStreamingStopRequestListener
      );
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_REMOVE_MEDIA_STOP_REQUEST_LISTENER,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_REMOVE_MEDIA_STOP_REQUEST_LISTENER
      );
    }
  };

  // media streaming start listener
  mediaStreamingStartRequestListener = async (message) => {
    let { roomId, data } = this.formatMessageToData(message);
    let { launchId, peerId, requestedStreams } = data;
    let requestedStreamObj = JSON.parse(requestedStreams);

    const config = {
      launchId,
      roomId,
      peerId,
      userRole: USER_ROLE.BROADCASTER
    };

    if (requestedStreamObj.webcam) {
      const webcamStream = MediaTrackService.getLocalWebcamStream();

      if (!webcamStream) {
        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [IN ][pageId: ${launchId}] [WEBCAM] [REQUEST IGNORED - STREAM NOT GRANTED]`
        );

        return;
      } 
    }

    if (requestedStreamObj.screen) {
      const screenshareStream = MediaTrackService.getLocalScreenshareStream();

      if (!screenshareStream) {
        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [IN ][pageId: ${launchId}] [SCREEN] [REQUEST IGNORED - STREAM NOT GRANTED]`
        );

        return;
      } 
    }

    await PeerConnectionManager.startPeerConnection(
      config,
      {
        sendOffer: this.sendPeerOffer,
        sendAnswer: this.sendPeerAnswer,
        sendIceCandidate: this.sendIceCandidate
      },
      requestedStreamObj
    );
  };

  // media streaming stop listener
  mediaStreamingStopRequestListener = async (message) => {
    let { data } = this.formatMessageToData(message);
    let { launchId, peerId } = data;


    LoggerService.info(
      LoggerService.MODULE.STREAMING,
      `[Streaming] [peerId: ${peerId}][launchId: ${launchId}] [STOP REQUEST SENT]`
    );

    await PeerConnectionManager.stopPeerConnection(peerId);
  };

  // --------------------------------

  formatDataToMessage(roomId, dataObj) {
    try {
      const data = JSON.stringify(dataObj);
      const messageObj = {
        roomId,
        data
      };
      const message = JSON.stringify(messageObj);
      return message;
    } catch (error) {
      throw new Error("Failed to format data to message");
    }
  }

  formatMessageToData(message) {
    try {
      const messageObj = JSON.parse(message);
      const roomId = messageObj.roomId;
      const data = messageObj.data;
      const dataObj = JSON.parse(data);

      return {
        roomId,
        data: dataObj
      };
    } catch (error) {
      throw new Error("Failed to format message to data");
    }
  }

  // listen to peer offer
  listenToPeerOffer = async () => {
    try {
      await this.addListener(EVENTS.PeerOffer, this.peerOfferListener);
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_LISTEN_TO_PEER_OFFER,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_LISTEN_TO_PEER_OFFER
      );
    }
  };

  // listen to peer answer
  listenToPeerAnswer = async () => {
    try {
      await this.addListener(EVENTS.PeerAnswer, this.peerAnswerListener);
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_LISTEN_TO_PEER_ANSWER,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_LISTEN_TO_PEER_ANSWER
      );
    }
  };

  // listen to ice candidates
  listenToIceCandidates = async () => {
    try {
      await this.addListener(EVENTS.IceCandidates, this.iceCandidatesListener);
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_LISTEN_TO_ICE_CANDIDATES,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_LISTEN_TO_ICE_CANDIDATES
      );
    }
  };

  // send peer offer
  sendPeerOffer = async (config, sdp, requestedStreamsObj, iceServersConfig) => {
    let launchId = config.launchId;
    let roomId = config.roomId;
    let peerId = config.peerId;

    let requestedStreams = JSON.stringify(requestedStreamsObj);
    let dataObj = {
      launchId,
      peerId,
      sdp,
      requestedStreams,
      iceServersConfig
    };

    try {
      const message = this.formatDataToMessage(roomId, dataObj);
      const status = await this.invoke(EVENTS.PeerOffer, message);

      return status;
    } catch (error) {
      if (Logger.isEnabled) {
        throw CustomErrorManager.ServiceError(
          CustomErrorCodes.LIVE_STREAMING_FAILED_TO_SEND_PEER_OFFER,
          CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_SEND_PEER_OFFER
        );
      }
    }
  };

  // send peer answer
  sendPeerAnswer = async (config, sdp) => {
    let launchId = config.launchId;
    let roomId = config.roomId;
    let peerId = config.peerId;

    let dataObj = {
      launchId,
      peerId,
      sdp
    };

    try {
      const message = this.formatDataToMessage(roomId, dataObj);
      const status = await this.invoke(EVENTS.PeerAnswer, message);

      return status;
    } catch (error) {
      if (Logger.isEnabled) {
        throw CustomErrorManager.ServiceError(
          CustomErrorCodes.LIVE_STREAMING_FAILED_TO_SEND_PEER_OFFER,
          CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_SEND_PEER_OFFER
        );
      }
    }
  };

  // send ice candidate
  sendIceCandidate = async (config, candidate) => {
    let launchId = config.launchId;
    let roomId = config.roomId;
    let peerId = config.peerId;

    let dataObj = {
      launchId,
      peerId,
      candidate
    };

    try {
      const message = this.formatDataToMessage(roomId, dataObj);
      const status = await this.invoke(EVENTS.IceCandidates, message);

      return status;
    } catch (error) {
      if (Logger.isEnabled) {
        throw CustomErrorManager.ServiceError(
          CustomErrorCodes.LIVE_STREAMING_FAILED_TO_SEND_ICE_CANDIDATES,
          CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_SEND_ICE_CANDIDATES
        );
      }
    }
  };

  // remove peer offer listener
  removePeerOfferListener = async () => {
    try {
      await this.removeListener(EVENTS.PeerOffer, this.peerOfferListener);
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_REMOVE_PEER_OFFER_LISTENER,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_REMOVE_PEER_OFFER_LISTENER
      );
    }
  };

  // remove peer answer listener
  removePeerAnswerListener = async () => {
    try {
      await this.removeListener(EVENTS.PeerAnswer, this.peerAnswerListener);
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_REMOVE_PEER_ANSWER_LISTENER,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_REMOVE_PEER_ANSWER_LISTENER
      );
    }
  };

  // remove ice candidates listener
  removeIceCandidatesListener = async () => {
    try {
      await this.removeListener(EVENTS.IceCandidates, this.iceCandidatesListener);
    } catch (error) {
      throw CustomErrorManager.ServiceError(
        CustomErrorCodes.LIVE_STREAMING_FAILED_TO_REMOVE_ICE_CANDIDATES_LISTENER,
        CustomErrorCodeMessages.LIVE_STREAMING_FAILED_TO_REMOVE_ICE_CANDIDATES_LISTENER
      );
    }
  };

  // peer offer listener
  peerOfferListener = async (message) => {
    let { roomId, data } = this.formatMessageToData(message);

    let { launchId, peerId, sdp, requestedStreams, iceServersConfig } = data;
    let requestedStreamObj = JSON.parse(requestedStreams);

    try {
      if (launchId == LiveStreaming.config.launchId) {
        const config = {
          launchId,
          roomId,
          peerId,
          userRole: USER_ROLE.VIEWER
        };

        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [peerId: ${peerId}] [OFFER RECEIVED]`
        );

        await PeerConnectionManager.startPeerConnection(
          config,
          {
            sendOffer: this.sendPeerOffer,
            sendAnswer: this.sendPeerAnswer,
            sendIceCandidate: this.sendIceCandidate
          },
          requestedStreamObj,
          iceServersConfig
        );

        let peerConnection = PeerConnectionManager.getPeerConnection(peerId);
        peerConnection.onReceivingOffer(sdp);
      }
    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${peerId}] [SIGNALING] [ERROR IN OFFER LISTENER]`,
        error
      );
    }
  };

  // peer answer listener
  peerAnswerListener = async (message) => {
    let { roomId, data } = this.formatMessageToData(message);
    let { launchId, peerId, sdp, mediaType } = data;

    try {
      let peerConnection = PeerConnectionManager.getPeerConnection(peerId);
      peerConnection.onReceivingAnswer(sdp, mediaType);
    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${peerId}] [SIGNALING] [ERROR IN ANSWER LISTENER]`,
        error
      );
    }
  };

  // ice candidates listener
  iceCandidatesListener = async (message) => {
    let { data } = this.formatMessageToData(message);
    let { peerId, candidate } = data;

    try {
      let peerConnection = PeerConnectionManager.getPeerConnection(peerId);
      if (peerConnection) {
        peerConnection.onRecevingIceCandidate(candidate);
      }

    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[peerId: ${peerId}] [SIGNALING] [ERROR IN ICE Candidate LISTENER]`,
        error
      );
    }
  };
}
